import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  AlertTriangle,
  Activity,
  ArrowUpRight,
  CheckCircle,
  XCircle,
  Clock,
  Monitor,
  Workflow,
  BarChart3,
  Zap,
  Calendar,
  Play
} from 'lucide-react'
import { api } from '../lib/api'
import { formatRelativeTime } from '../lib/utils'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
// QuickProviderSwitch removed - using Anchor Browser exclusively

export function Dashboard() {
  const queryClient = useQueryClient()

  const { isLoading: isLoadingOrg } = useQuery({
    queryKey: ['organization', 'current'],
    queryFn: () => api.getCurrentOrganization(),
    staleTime: 1000 * 30, // 30 seconds
    refetchInterval: 1000 * 60, // Refetch every minute
  })

  const { data: recentRuns, isLoading: isLoadingRuns } = useQuery({
    queryKey: ['workflow-runs', 'recent'],
    queryFn: () => api.getWorkflowRuns({ limit: 10 }),
    staleTime: 1000 * 30, // 30 seconds
    refetchInterval: 1000 * 30, // Refetch every 30 seconds for real-time feel
  })

  const { data: workflows } = useQuery({
    queryKey: ['workflows', 'summary'],
    queryFn: () => api.getWorkflows(),
    staleTime: 1000 * 60, // 1 minute
  })



  // Calculate real-time metrics from workflow runs
  const runs = recentRuns?.runs || []
  const totalRuns = runs.length
  const runningRuns = runs.filter(r => r.status === 'running').length
  const successfulRuns = runs.filter(r => r.status === 'success').length
  const failedRuns = runs.filter(r => r.status === 'failed').length
  const successRate = totalRuns > 0 ? Math.round((successfulRuns / totalRuns) * 100) : 0

  const totalWorkflows = workflows?.workflows?.length || 0

  const handleRestart = async (run: any) => {
    try {
      // Start the workflow again using the workflow ID
      await api.startWorkflow(run.workflow_id)
      // Refetch the workflow runs to update the UI
      queryClient.invalidateQueries({ queryKey: ['workflow-runs'] })
    } catch (error) {
      console.error('Failed to restart workflow:', error)
    }
  }

  const stats = [
    {
      name: 'Total Workflows',
      value: totalWorkflows,
      icon: Workflow,
      description: 'Automation workflows',
      trend: '+12%',
      color: 'bg-blue-500'
    },
    {
      name: 'Success Rate',
      value: `${successRate}%`,
      icon: CheckCircle,
      description: 'Recent executions',
      trend: successRate >= 80 ? '+5%' : 'Low',
      color: successRate >= 80 ? 'bg-green-500' : 'bg-yellow-500'
    },
    {
      name: 'Running Now',
      value: runningRuns,
      icon: Activity,
      description: 'Active executions',
      trend: runningRuns > 0 ? 'Live' : 'Idle',
      color: runningRuns > 0 ? 'bg-orange-500' : 'bg-gray-500'
    },
    {
      name: 'Failed Runs',
      value: failedRuns,
      icon: AlertTriangle,
      description: 'Need attention',
      trend: failedRuns > 0 ? 'Alert' : 'Good',
      color: failedRuns > 0 ? 'bg-red-500' : 'bg-green-500'
    },
  ]



  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="container mx-auto p-6 space-y-8">
        {/* Executive Header */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg">
                <BarChart3 className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-slate-900">Dashboard</h1>
                <p className="text-slate-600 mt-1">
                  Real-time overview of your automation platform
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                Live Data
              </div>
              <Link to="/runs">
                <Button>
                  <Monitor className="h-4 w-4 mr-2" />
                  View Details
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Enhanced Stats Cards */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat) => (
            <Card key={stat.name} className="bg-white shadow-sm border-0 overflow-hidden">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-600 mb-1">
                      {stat.name}
                    </p>
                    {isLoadingOrg || isLoadingRuns ? (
                      <Skeleton className="h-8 w-16" />
                    ) : (
                      <div className="text-3xl font-bold text-slate-900 mb-1">
                        {stat.value}
                      </div>
                    )}
                    <p className="text-xs text-slate-500">
                      {stat.description}
                    </p>
                    <div className="flex items-center gap-1 mt-2">
                      <span className={`text-xs px-2 py-1 rounded-full text-white ${stat.color}`}>
                        {stat.trend}
                      </span>
                    </div>
                  </div>
                  <div className={`p-3 rounded-full ${stat.color} bg-opacity-10`}>
                    <stat.icon className={`h-6 w-6 ${stat.color.replace('bg-', 'text-')}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Executive Overview Grid */}
        <div className="grid gap-6 lg:grid-cols-3">
          {/* System Health */}
          <Card className="bg-white shadow-sm">
            <CardHeader className="border-b bg-slate-50">
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                System Health
              </CardTitle>
              <CardDescription>
                Real-time platform status
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">API Status</span>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-sm font-medium text-green-600">Healthy</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Browser Sessions</span>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span className="text-sm font-medium text-blue-600">{runningRuns} Active</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Queue Status</span>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-sm font-medium text-green-600">Normal</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card className="lg:col-span-2 bg-white shadow-sm">
            <CardHeader className="border-b bg-slate-50">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Recent Activity
                </CardTitle>
                <Link to="/runs">
                  <Button variant="outline" size="sm">
                    <Monitor className="h-4 w-4 mr-2" />
                    View All Runs
                  </Button>
                </Link>
              </div>
              <CardDescription>
                Latest workflow executions and system events
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              {isLoadingRuns ? (
                <div className="space-y-4">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-6 w-16" />
                        <div className="space-y-1">
                          <Skeleton className="h-4 w-32" />
                          <Skeleton className="h-3 w-24" />
                        </div>
                      </div>
                      <Skeleton className="h-8 w-16" />
                    </div>
                  ))}
                </div>
              ) : runs.length ? (
                <div className="space-y-3">
                  {runs.slice(0, 5).map((run) => {
                    const statusConfigs = {
                      success: { color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle },
                      failed: { color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle },
                      running: { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Activity },
                      queued: { color: 'bg-gray-100 text-gray-700 border-gray-200', icon: Clock },
                    }
                    const statusConfig = statusConfigs[run.status as keyof typeof statusConfigs] || statusConfigs.queued

                    const StatusIcon = statusConfig.icon

                    return (
                      <div key={run.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${statusConfig.color}`}>
                            <StatusIcon className="h-3 w-3" />
                            {run.status}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-900">
                              {run.workflow_name || `Run #${run.id}`}
                            </p>
                            <p className="text-xs text-slate-500">
                              {formatRelativeTime(new Date(run.created_at).getTime())} • {run.trigger}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {run.status === 'success' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.preventDefault()
                                handleRestart(run)
                              }}
                            >
                              <Play className="h-3 w-3 mr-1" />
                              Restart
                            </Button>
                          )}
                          <Link to={`/workflow-runs/${run.id}`}>
                            <Button variant="ghost" size="sm">
                              <ArrowUpRight className="h-4 w-4" />
                            </Button>
                          </Link>
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Activity className="mx-auto h-12 w-12 text-slate-400" />
                  <h3 className="mt-2 text-sm font-medium text-slate-900">No recent activity</h3>
                  <p className="mt-1 text-sm text-slate-500">
                    Workflow executions will appear here once you start running workflows.
                  </p>
                  <Link to="/workflows" className="mt-4 inline-block">
                    <Button size="sm">
                      <Workflow className="h-4 w-4 mr-2" />
                      Create Workflow
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card className="bg-white shadow-sm">
          <CardHeader className="border-b bg-slate-50">
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5" />
              Quick Actions
            </CardTitle>
            <CardDescription>
              Common tasks and shortcuts
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid gap-4 md:grid-cols-3">
              <Link to="/workflows/new">
                <Button className="w-full justify-start" variant="outline">
                  <Workflow className="h-4 w-4 mr-2" />
                  Create Workflow
                </Button>
              </Link>
              <Link to="/runs">
                <Button className="w-full justify-start" variant="outline">
                  <Monitor className="h-4 w-4 mr-2" />
                  Monitor Runs
                </Button>
              </Link>
              <Link to="/scheduled-tasks">
                <Button className="w-full justify-start" variant="outline">
                  <Calendar className="h-4 w-4 mr-2" />
                  View Schedules
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
